import React, { useEffect, useMemo, useState } from "react";
import { Card, Empty } from "../../dashboard/primitives";
import MessagesThread from "../../dashboard/MessagesThread";
import { subscribeAllProjects, subscribeClients } from "../../projects/api";
import { SkeletonList, SkeletonMediaRow } from "../../components/Skeleton";
import { useMinLoading } from "../../lib/useMinLoading";

const fmtTs = (ts) => {
  if (!ts?.toDate) return "";
  const d = ts.toDate();
  const now = new Date();
  const diff = now - d;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
};

const MessagesSection = () => {
  const [projects, setProjects] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [clients, setClients] = useState([]);
  const [activeId, setActiveId] = useState(null);

  useEffect(
    () =>
      subscribeAllProjects((v) => {
        setProjects(v);
        setLoaded(true);
      }),
    []
  );

  const showSkeleton = useMinLoading(!loaded);
  useEffect(() => subscribeClients(setClients), []);

  const usersByUid = useMemo(
    () => Object.fromEntries(clients.map((c) => [c.id, c])),
    [clients]
  );

  const sorted = useMemo(
    () =>
      [...projects]
        .filter((p) => !p.archived)
        .sort((a, b) => {
          const at = a.lastMessageAt?.toMillis?.() || 0;
          const bt = b.lastMessageAt?.toMillis?.() || 0;
          return bt - at;
        }),
    [projects]
  );

  useEffect(() => {
    if (!activeId && sorted.length) setActiveId(sorted[0].id);
  }, [sorted, activeId]);

  const active = sorted.find((p) => p.id === activeId);
  const activeOwner = active && usersByUid[active.ownerUid];

  return (
    <Card title="Messages">
      {showSkeleton ? (
        <SkeletonList count={5} ariaLabel="Loading messages">
          <SkeletonMediaRow />
        </SkeletonList>
      ) : sorted.length === 0 ? (
        <Empty>No active projects</Empty>
      ) : (
        <div className="fw-messages">
          <aside className="fw-messages__list">
            {sorted.map((p) => {
              const owner = usersByUid[p.ownerUid];
              const isActive = p.id === activeId;
              return (
                <button
                  key={p.id}
                  type="button"
                  className={`fw-messages__item ${isActive ? "is-active" : ""}`}
                  onClick={() => setActiveId(p.id)}
                >
                  <div className="fw-messages__item-title">{p.title}</div>
                  <div className="fw-messages__item-meta">
                    <span>{owner?.displayName || owner?.email || p.ownerUid?.slice(0, 6)}</span>
                    <span>{fmtTs(p.lastMessageAt)}</span>
                  </div>
                </button>
              );
            })}
          </aside>
          <div className="fw-messages__pane">
            <MessagesThread
              projectId={active?.id}
              projectTitle={
                active
                  ? `${active.title} · ${activeOwner?.displayName || activeOwner?.email || "Client"}`
                  : null
              }
            />
          </div>
        </div>
      )}
    </Card>
  );
};

export default MessagesSection;
